import React, { useState, useEffect, useCallback, JSX, useMemo } from 'react';
import { colors, useMediaQuery } from '../../Styles';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Components';
import { ButtonTypes } from '../../Definitions';

export type SortOption = 'id' | 'name';

interface CardsView_Props<T> {
  data: T[];
  card: (item: T, index: number) => JSX.Element;
  sortBy?: SortOption;
  searchTerm?: string;
  getName?: (item: T) => string;
  getId?: (item: T) => string | number;
  cardBtn?: {
    content?: string;
    subContent?: string;
    onCardBtnClick?: () => void;
    isCardBtnDisabled?: boolean;
    height?: string;
  };
}

export default function CardsView<T>({
  data,
  card,
  sortBy,
  searchTerm,
  getName = () => '',
  getId = () => '',
  cardBtn
}: CardsView_Props<T>) {
  const { t } = useTranslation();
  const [columns, setColumns] = useState(1);

  // Media Queries for responsive columns
  const columnCount4 = useMediaQuery(`(min-width: 1700px)`);
  const columnCount3 = useMediaQuery(`(min-width: 1360px)`);
  const columnCount2 = useMediaQuery(`(min-width: 700px)`);

  useEffect(() => {
    const getColumnCount = () => {
      if (columnCount4) return 4;
      if (columnCount3) return 3;
      if (columnCount2) return 2;
      return 1;
    };
    setColumns(getColumnCount());
  }, [columnCount2, columnCount3, columnCount4]);

  const reorder = useCallback((arr: T[], cols: number) => {
    const out: T[] = [];
    for (let col = 0; col < cols; col++) {
      for (let i = 0; i < arr.length; i += cols) {
        const item = arr[i + col];
        if (item !== undefined) {
          out.push(item);
        }
      }
    }
    return out;
  }, []);

  const sortedData = useMemo(() => {
    const trimmedSearch = searchTerm?.trim().toLowerCase() ?? '';

    const filtered = data.filter(item => {
      const name = getName(item).toLowerCase();
      const id = getId(item).toString();
      return name.includes(trimmedSearch) || id.includes(trimmedSearch);
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        return getName(a).localeCompare(getName(b));
      } else {
        return Number(getId(a)) - Number(getId(b));
      }
    });

    return reorder(sorted, columns);
  }, [data, columns, reorder, sortBy, searchTerm, getName, getId]);

  return (
    <div style={{ columnCount: columns, columnGap: '20px', width: '100%' }}>
      {cardBtn?.onCardBtnClick && (
        <div style={{ position: 'relative' }}>
          <Button
            content={cardBtn?.content ?? ''}
            subContent={cardBtn?.subContent}
            onClick={cardBtn?.isCardBtnDisabled ? undefined : cardBtn.onCardBtnClick}
            buttonType={ButtonTypes.cardButton}
            overrides={{
              baseButton: {height: cardBtn?.height ?? "200px"}
            }}
          />
          {cardBtn?.isCardBtnDisabled && (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: colors.blackPrimary + 'cc',
                position: 'absolute',
                top: '0px',
                left: '0px',
                borderRadius: '10px',
              }}
            />
          )}
        </div>
      )}
      {sortedData.map((item, index) => card(item, index))}
    </div>
  );
}
